/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Stack } from "@mui/system";
import { Box, IconButton, MenuItem, Select, Typography, FormControl, OutlinedInput, InputAdornment, Button, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { getTransportOrderProducts } from "../../../services/transportOrderProductsService";
import Grid2 from "@mui/material/Unstable_Grid2";
import { TransportOrderProductListItemDto } from "../../../models/transport/transportOrderProduct";
import { object, array, string, number } from "yup";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import { TransportOrderListItemDto } from "../../../models/transport/transportOrder";
import * as storeActions from "../../../models/store/storeActions";
import { Base } from "../../../framework/base";

interface ISidebarOrderProductsProps {
    autoFocusFirst: boolean;
    transportOrder: TransportOrderListItemDto;
    onClose: () => void;
    onProductsAdded: (products: TransportOrderProductListItemDto[], errors: any) => void;
}

export const SidebarOrderProducts = ({
    autoFocusFirst,
    transportOrder,
    onClose,
    onProductsAdded,
}: ISidebarOrderProductsProps) => {
    const { t } = useTranslation();
    const [products, setProducts] =
        useState<TransportOrderProductListItemDto[]>();
    const [orderAddedProducts, setOrderAddedProducts] =
        useState<string[]>();
    const MAX_QUANTITY: number = 9999;

    const validationSchema = object().shape({
        transportProducts: array().of(
            object().shape({
                id: string().required(t("validation.fieldIsRequired", { field: t("transport.order.productName") })),
                quantity: number()
                    .min(1, t("validation.fieldMinValue", { field: t("transport.order.quantity"), value: 1 }))
                    .max(MAX_QUANTITY, t("validation.fieldMaxValue", { field: t("transport.order.quantity"), value: MAX_QUANTITY }))
                    .required(t("validation.fieldIsRequired", { field: t("transport.order.quantity") }))
            })
        )
    });

    const handleChange = (
        values: { transportProducts: { id: string; quantity: number }[] },
        errors: { transportProducts: any[] },
    ) => {
        const addedProducts = values.transportProducts.map(
            ({ id, quantity }) => {
                const productDetails = products.find((p) => p.id === id);
                return {
                    ...productDetails,
                    quantity,
                };
            }
        );

        // Keep track of product IDs that are added to order
        const aProducts = [];
        addedProducts.forEach(p => {
            if (p) {
                aProducts.push(p.id);
            }
        });
        setOrderAddedProducts(aProducts);

        onProductsAdded(
            addedProducts,
            errors.transportProducts
        );
    };

    useEffect(() => {
        const fetchOrderProducts = async() => {
            try {
                const response = await getTransportOrderProducts();
                const products = response.data;
                setProducts(products);
            } catch (error) {
                console.error("Error fetching order products:", error);
            }
        };
        fetchOrderProducts().catch((error) =>
            console.error("Error in fetchOrderProducts:", error)
        );
    }, []);

    if (!products) return (
        <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
        </Box>
    );

    return (
        <Formik
            initialValues={{
                transportProducts: transportOrder?.id === "new" && autoFocusFirst ? [{ id: "", quantity: 1 }] : transportOrder?.products || [],
            }}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={() => {}}
        >
            {({ values, setFieldValue }) => {
                const hasEmptyFields = values.transportProducts.some(p => !p.id || !p.quantity || p.quantity === 0);

                return (
                    <>
                        <FormikContextContainer onChange={handleChange} />
                        <Form>
                            <Stack spacing={2} sx={{ width: "100%" }} fontSize="small">
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="h4">{t("transport.order.products")}</Typography>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <IconButton className="close-panel-button" size="small" onClick={onClose}>
                                            <CloseIcon color="primary" />
                                        </IconButton>
                                    </Box>
                                </Stack>

                                <Grid2>
                                    {values.transportProducts.map((product, index) => (
                                        <Stack key={index} direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }} className={"mb-2"}>
                                            <FormControl sx={{ flex: 1 }}>
                                                <span>{t("transport.order.productName")}</span>
                                                <Field as={Select}
                                                    autoFocus={autoFocusFirst && index === 0}
                                                    name={`transportProducts.${index}.id`}
                                                    displayEmpty
                                                    onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                                                        const selectedProductId = e.target.value as string;
                                                        setFieldValue(`transportProducts.${index}.id`, selectedProductId).catch((err: Error) => storeActions.showErrorMessage(err.message));
                                                    }}
                                                    size="small"
                                                >
                                                    <MenuItem value="" />
                                                    {products.map((product) => (
                                                        <MenuItem
                                                            key={product.id}
                                                            value={product.id}
                                                            disabled={orderAddedProducts?.includes(product.id)}
                                                        >
                                                            <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                                <Box>{product.name}</Box>
                                                                <Box sx={{ fontSize: "14px", color: "grey" }}>{Base.formatPrice(product.unitPrice)}&euro;/{product.unitName}</Box>
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Field>
                                                <ErrorMessage name={`transportProducts.${index}.id`} component="div" className="text-error" />
                                            </FormControl>
                                            <FormControl sx={{ width: "100px" }}>
                                                <span>{t("transport.order.quantity")}</span>
                                                <Field
                                                    as={OutlinedInput}
                                                    name={`transportProducts.${index}.quantity`}
                                                    type="number"
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        setFieldValue(`transportProducts.${index}.quantity`, e.target.value)
                                                    }
                                                    endAdornment={
                                                        <InputAdornment position="end">
                                                            {products.find((p) => p.id === product.id)?.unitName || "-"}
                                                        </InputAdornment>
                                                    }
                                                    sx={{ backgroundColor: "white" }}
                                                    size="small"
                                                />
                                                <ErrorMessage name={`transportProducts.${index}.quantity`} component="div" className="text-error" />
                                            </FormControl>

                                            <IconButton
                                                onClick={() => setFieldValue("transportProducts", values.transportProducts.filter((_, i) => i !== index))}
                                                sx={{ color: "primary.main" }}
                                            >
                                                <RemoveIcon />
                                            </IconButton>
                                        </Stack>
                                    ))}

                                    <Button
                                        variant="text"
                                        startIcon={<AddIcon />}
                                        onClick={() => setFieldValue("transportProducts", [...values.transportProducts, { id: "", quantity: 1 }])}
                                        disabled={hasEmptyFields}
                                        sx={{ mt: 2, alignSelf: "flex-start" }}
                                    >
                                        {t("common.addNew")}
                                    </Button>
                                </Grid2>
                            </Stack>
                        </Form>
                    </>
                );
            }}
        </Formik>
    );
};

interface FormikContextContainerProps {
    onChange: (values: any, errors: any) => void;
}

export const FormikContextContainer = ({
    onChange,
}: FormikContextContainerProps) => {
    const formikContext = useFormikContext();

    useEffect(() => {
        if (!!formikContext?.values) {
            onChange(formikContext.values, formikContext.errors);
        }
    }, [formikContext?.values, formikContext?.errors]);

    return null;
};
